/**
 * ACF Tourism — user-visible booking references (agent + B2C).
 *
 * Product rule: agent and B2C UIs must never show Tripjack supplier session
 * ids (e.g. TJ…) or other supplier booking keys. Only our system booking id
 * (ACF…) is shown to users. Supplier ids may still be used in background API
 * calls (e.g. release-hold, Tripjack /review).
 */

export function isTripjackSupplierBookingId(value) {
  if (value == null || value === '') return false;
  const s = String(value).trim();
  return s.startsWith('TJ');
}

/**
 * @param {Record<string, unknown>|null|undefined} entity
 * @returns {string}
 */
export function getUserFacingBookingId(entity) {
  if (!entity || typeof entity !== 'object') return '—';
  const ordered = [
    entity.acf_booking_id,
    entity.acfBookingId,
    entity.booking_id,
    entity.bookingId,
    entity.internal_booking_id,
    entity.id,
  ];
  for (const v of ordered) {
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (s.startsWith('ACF')) return s;
  }
  for (const v of ordered) {
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (s && !isTripjackSupplierBookingId(s)) return s;
  }
  return '—';
}

/**
 * Id for `/api/bookings/...`, `/booking-details/:id`, e-ticket download, etc.
 * Prefer ACF `booking_id`; never use a TJ supplier id in the path.
 */
export function getApiBookingRouteId(entity) {
  if (!entity || typeof entity !== 'object') return '';
  const bid = String(entity.booking_id || '').trim();
  if (bid.startsWith('ACF')) return bid;
  const bid2 = String(entity.bookingId || '').trim();
  if (bid2.startsWith('ACF')) return bid2;
  const id = String(entity.id || '').trim();
  if (id.startsWith('ACF')) return id;
  if (bid && !isTripjackSupplierBookingId(bid)) return bid;
  if (id && !isTripjackSupplierBookingId(id)) return id;
  if (bid2 && !isTripjackSupplierBookingId(bid2)) return bid2;
  return '';
}

/**
 * Canonical ops keys for admin deep links (Support, Monitor, Refunds).
 * Mirrors backend `resolve_booking_keys`.
 */
export function resolveOpsBookingKeys(entity, ticketBookingId) {
  const fallback = ticketBookingId != null && ticketBookingId !== ''
    ? String(ticketBookingId).trim()
    : '';
  if (!entity || typeof entity !== 'object') {
    const lookupIds = fallback ? [fallback] : [];
    return {
      canonicalId: fallback,
      displayId: fallback,
      lookupIds,
    };
  }
  const ordered = [
    entity.acf_booking_id,
    entity.acfBookingId,
    entity.booking_id,
    entity.bookingId,
    entity.id,
    fallback,
  ];
  let acf = '';
  for (const v of ordered) {
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (s.startsWith('ACF')) {
      acf = s;
      break;
    }
  }
  let canonical = acf;
  if (!canonical) {
    for (const v of ordered) {
      if (v == null || v === '') continue;
      const s = String(v).trim();
      if (s && !isTripjackSupplierBookingId(s)) {
        canonical = s;
        break;
      }
    }
  }
  const lookupIds = [];
  for (const v of [canonical, fallback, entity.booking_id, entity.id, entity.acf_booking_id]) {
    if (v == null || v === '') continue;
    const s = String(v).trim();
    if (s && !lookupIds.includes(s)) lookupIds.push(s);
  }
  return {
    canonicalId: canonical || lookupIds[0] || '',
    displayId: canonical || lookupIds[0] || '',
    lookupIds,
  };
}

/** After POST /api/tripjack/book — persist only ACF id into UI state. */
export function acfIdFromTripjackBookResponse(resp) {
  if (!resp || typeof resp !== 'object') return '';
  const s = getUserFacingBookingId({
    bookingId: resp.bookingId,
    booking_id: resp.booking_id,
  });
  return s !== '—' ? s : '';
}
